import { FC, useState, useMemo } from 'react';
import ModalComponent from 'v3/common/components/Modal';
import TipButton from './components/TipButton';
import useTips from './hooks/useTips';
import { ButtonConfig, TIP_CATEGORY } from './types';
import MultiImage from './components/MultiImage';
import Video from './components/Video';
import Image from './components/Image';
import ApplicationDomainUtility from 'v3/common/utils/applicationDomain';

const TipsModal: FC = () => {
  const [activeSlideIndex, setActiveSlideIndex] = useState<number>(0);
  const { tip, showModal, handleCloseModal } = useTips();
  const { title, description } = tip || {};

  // Get button config based on category and active slide
  const buttonConfig = useMemo((): ButtonConfig | null => {
    if (!tip || !tip.content) return null;

    // For image and video, content is an object
    if (tip.category === TIP_CATEGORY.IMAGE || tip.category === TIP_CATEGORY.VIDEO) {
      const { content } = tip;
      if (content?.btn_text && content?.btn_url) {
        return {
          btn_text: content.btn_text,
          btn_url: content.btn_url,
        };
      }
    }

    // For multiImage, content is an array - use the active slide's button config
    if (tip.category === TIP_CATEGORY.MULTI_IMAGE) {
      const activeSlide = tip.content[activeSlideIndex];
      if (activeSlide?.btn_text && activeSlide?.btn_url) {
        return {
          btn_text: activeSlide.btn_text,
          btn_url: activeSlide.btn_url,
        };
      }
    }

    return null;
  }, [tip, activeSlideIndex]);

  const renderMedia = useMemo(() => {
    if (!tip || !tip.content) return null;

    switch (tip.category) {
      case TIP_CATEGORY.IMAGE:
        return <Image content={tip.content} />;
      case TIP_CATEGORY.MULTI_IMAGE:
        return <MultiImage content={tip.content} onSlideChange={setActiveSlideIndex} />;
      case TIP_CATEGORY.VIDEO:
        return <Video content={tip.content} />;
      default:
        return null;
    }
  }, [tip, setActiveSlideIndex]);

  if (!showModal) return null;
  if (!tip || !tip.id) return null;
  if (ApplicationDomainUtility.isB2b) return null;

  return (
    <ModalComponent isOpen={showModal} onClose={handleCloseModal} className="tips-modal">
      <>
        <div className="tips-modal__media-container">{renderMedia}</div>

        <div className="tips-modal__main-contents">
          <div className="tips-modal__title">
            <h4>{title}</h4>
          </div>
          <div className="tips-modal__description">{description}</div>
          {buttonConfig && (
            <TipButton
              buttonConfig={buttonConfig}
              onClick={() => {
                handleCloseModal();
                window.open(buttonConfig.btn_url, '_blank', 'noopener,noreferrer');
              }}
            />
          )}
        </div>
      </>
    </ModalComponent>
  );
};

export default TipsModal;
